// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import styles from './PlayPanel.module.css';
import { PlayParameter, sliderRange } from './play-parameters';

interface PlayPanelProps {
  /** The model's tweakable constants (see play-parameters.modelParameters). */
  parameters: readonly PlayParameter[];
  /** Current override values keyed by ident; falls back to the parameter's value. */
  overrides: ReadonlyMap<string, number>;
  onOverrideChange: (ident: string, value: number) => void;
  onResimulate: () => void;
  /** True while a re-simulation is in flight (disables the button). */
  simulating?: boolean;
}

/** Compact numeric formatting for the slider read-out. */
function formatValue(v: number): string {
  if (v === 0) {
    return '0';
  }
  const abs = Math.abs(v);
  if (abs >= 1000 || abs < 0.01) {
    return v.toPrecision(3);
  }
  return String(Math.round(v * 1000) / 1000);
}

/**
 * The Play panel (Activities > Play tab, MVP WS2): a slider per model constant
 * plus a Re-simulate button. Moving a lever changes an override; Re-simulate runs
 * the model with those overrides so behavior updates -- the "debug your intuition"
 * loop. Per the WS2 decision, re-simulation is button-driven, not live-on-drag
 * (the engine run is batch); live-on-drag can follow.
 *
 * Presentation-only: the Editor owns the parameter list, the override map, and
 * the engine run; changes flow back through the callbacks.
 */
export class PlayPanel extends React.PureComponent<PlayPanelProps> {
  render(): React.ReactNode {
    const { parameters, overrides, onOverrideChange, onResimulate, simulating } = this.props;

    if (parameters.length === 0) {
      return (
        <div className={styles.panel}>
          <div className={styles.empty}>This model has no adjustable parameters yet.</div>
        </div>
      );
    }

    return (
      <div className={styles.panel}>
        <div className={styles.intro}>Move a lever, then re-simulate to watch behavior change.</div>
        <div className={styles.sliders}>
          {parameters.map((param) => {
            const value = overrides.get(param.ident) ?? param.value;
            const range = sliderRange(param.value);
            return (
              <div key={param.ident} className={styles.slider}>
                <label className={styles.label} htmlFor={`play-${param.ident}`}>
                  <span className={styles.name}>{param.name}</span>
                  <span className={styles.value}>{formatValue(value)}</span>
                </label>
                <input
                  id={`play-${param.ident}`}
                  type="range"
                  className={styles.range}
                  min={range.min}
                  max={range.max}
                  step={range.step}
                  value={value}
                  aria-label={param.name}
                  onChange={(e) => onOverrideChange(param.ident, Number(e.currentTarget.value))}
                />
              </div>
            );
          })}
        </div>
        <button type="button" className={styles.resimulate} disabled={simulating} onClick={onResimulate}>
          {simulating ? 'Simulating…' : 'Run simulation'}
        </button>
      </div>
    );
  }
}
